import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { InstallOffer } from './InstallOffer';
import { useMeta } from './MetaContext';
import { accountItems, footerItems, navItems, type IconName, type NavItem as Item } from './nav';
import { ShellConnectionNotice } from './PageStates';
import { TestPanel } from './TestPanel';
import { Wordmark } from './Wordmark';

/** One icon set for the whole product: Bootstrap Icons, by name. */
export function Icon({ name }: { name: IconName }) {
  return <i className={`bi bi-${name}`} aria-hidden="true" />;
}

export interface ShellProps {
  /** The page's name; the browser tab keeps the platform's. */
  title: ReactNode;
  /** A small line above the title (a section, a code). */
  kicker?: ReactNode;
  /** Chips beside the title: state, outcome, counts. */
  chips?: ReactNode;
  /** Buttons and links for the page, right of the title on desktop, under it on phones. */
  actions?: ReactNode;
  /** `wide` for the designer and the direction page; `narrow` for reading and forms. */
  width?: 'normal' | 'wide' | 'narrow';
  /**
   * The page's own header strip in place of the title, chips and actions: the
   * live session's (UI doc 5.1), pinned to the top of the page while it scrolls.
   */
  header?: ReactNode;
  /**
   * The page's own items for the phone's bottom bar, three at most: the live
   * session's Now, Map and Roster (UI doc 4). The account's item follows them
   * and "More" still carries every other destination.
   */
  bar?: BarItem[];
  /**
   * What the way into the account is called on this page, in place of the
   * rail's and the bar's plain "Entra": beside a live game that word read as
   * joining it (the session page, the owner's review of 2026-10-12).
   */
  signInLabel?: string;
  children: ReactNode;
}

/** One of a page's own bottom-bar buttons: it acts on the page, it does not leave it. */
export interface BarItem {
  key: string;
  label: string;
  icon: IconName;
  onClick: () => void;
  /** Marks the item the page is showing, the way a route marks its link. */
  active?: boolean;
}

/**
 * Every page's frame (UI doc, section 4): a left rail on desktop, a bottom bar on phones,
 * one page header with the title, its chips and its actions. The brand is the rail's first
 * child so it stays put while a tall page scrolls (the rail is sticky); on phones the rail
 * shows nothing but the brand, at the top left, and the bottom bar carries the navigation.
 *
 * The rail is hidden on a phone, so anything the bottom bar does not carry used to be
 * reachable only by typing its address — new mission, catalogue, servers, the panel,
 * admin, settings and even signing out (found by the owner on 2026-09-08). The bar keeps
 * four destinations and a fifth button opens the rest as a sheet, with the same role
 * gating: the sheet holds whatever `navItems` and `accountItems` returned and the bar did
 * not take, so a link added to the rail is never again a link a phone cannot reach.
 *
 * Under the page, on desktop only, a footer in small muted type carries what the rail
 * lost on 2026-09-25: the changelog, the public archive for a member, the status page
 * for an admin (`footerItems`). It is not in the sheet on purpose: the owner asked for
 * nothing of it on a phone.
 *
 * A page can bring its own `header`, a strip pinned to the top of the page instead of
 * the title block, and its own `bar` items, which act on the page (the live session's
 * Now, Map and Roster, UI doc 4); the account's item and "More" follow them, so the rest
 * of the product stays one tap away.
 */
export function Shell({
  title,
  kicker,
  chips,
  actions,
  width = 'normal',
  header,
  bar: pageBar,
  signInLabel,
  children,
}: ShellProps) {
  const { meta } = useMeta();
  const { me } = useAuth();
  const items = navItems(me, meta.panelHost);
  const account = accountItems(me).map((i) =>
    signInLabel && i.to === '/signin' ? { ...i, label: signInLabel } : i,
  );
  const footer = footerItems(me);
  const all = [...items, ...account];
  // A page with its own bar keeps the account's item beside it (Me, or the way
  // in); everything else goes to the sheet, so nothing becomes unreachable.
  const bar = pageBar
    ? account.filter((i) => i.bar).slice(0, 1)
    : all.filter((i) => i.bar).slice(0, 4);
  const rest = all.filter((i) => !bar.includes(i));
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButton = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLElement>(null);
  const { pathname, search } = useLocation();
  // The way in comes back to the page it was taken from (UI doc, principle 9):
  // a visitor who opened a session's link signs in and is on the session again.
  const back =
    pathname === '/' || pathname === '/signin'
      ? ''
      : `?next=${encodeURIComponent(pathname + search)}`;
  const target = (i: Item) => (i.to === '/signin' ? `/signin${back}` : i.to);

  // Following a link closes the sheet: the page under it has already changed. Only a change
  // closes it: run on mount too, this effect could still be pending when a click on "Més"
  // landed right after the page appeared, and then it closed the sheet that click had opened.
  const shownPath = useRef(pathname);
  useEffect(() => {
    if (shownPath.current === pathname) return;
    shownPath.current = pathname;
    setMoreOpen(false);
  }, [pathname]);

  // Escape closes it and hands the focus back to the button that opened it, the
  // same contract as ConfirmDialog's.
  useEffect(() => {
    if (!moreOpen) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setMoreOpen(false);
      moreButton.current?.focus();
    }
    sheet.current?.querySelector<HTMLElement>('a, button')?.focus();
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [moreOpen]);

  const link = (i: Item) =>
    i.external ? (
      <a key={i.to} href={i.to} className="navitem" target="_blank" rel="noreferrer">
        <Icon name={i.icon} />
        <span>{i.label}</span>
      </a>
    ) : (
      <NavLink key={i.to} to={target(i)} end={i.end} className="navitem">
        <Icon name={i.icon} />
        <span>{i.label}</span>
      </NavLink>
    );
  return (
    <div className={`shell ${width}`}>
      <nav className="rail" aria-label={ca.nav.label}>
        <Link to="/" className="brand" aria-label={meta.name}>
          <span className="wordmark small">
            <Wordmark name={meta.name} small />
          </span>
        </Link>
        {items.map(link)}
        <span className="grow" />
        <InstallOffer />
        {me.authenticated && <p className="muted small who">{me.displayName ?? me.handle ?? ''}</p>}
        {account.map(link)}
        {me.authenticated && (
          <Link to="/signout" className="navitem">
            <Icon name="box-arrow-right" />
            <span>{ca.nav.signOut}</span>
          </Link>
        )}
      </nav>
      {!header && (
        <header className="pagehead">
          <div className="titles">
            {kicker && <p className="kicker muted small">{kicker}</p>}
            <h1>{title}</h1>
            {chips && <div className="row chips">{chips}</div>}
          </div>
          {actions && <div className="row actions">{actions}</div>}
        </header>
      )}
      <main className={header ? 'page with-strip' : 'page'}>
        {header && <header className="strip">{header}</header>}
        <ShellConnectionNotice />
        {children}
      </main>
      <footer className="shellfoot">
        <nav aria-label={ca.nav.footer}>
          {footer.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end}>
              {i.label}
            </NavLink>
          ))}
        </nav>
      </footer>
      <nav className="bottombar" aria-label={ca.nav.label}>
        {pageBar?.slice(0, 3).map((b) => (
          <button
            type="button"
            key={b.key}
            className={`navitem page-item ${b.active ? 'active' : ''}`}
            aria-current={b.active ? 'true' : undefined}
            onClick={b.onClick}
          >
            <Icon name={b.icon} />
            <span>{b.label}</span>
          </button>
        ))}
        {bar.map((i) => (
          <NavLink key={i.to} to={target(i)} end={i.end} className="navitem">
            <Icon name={i.icon} />
            <span>{i.to.startsWith('/u/') ? ca.nav.me : i.label}</span>
          </NavLink>
        ))}
        {rest.length > 0 && (
          <button
            type="button"
            className="navitem more"
            ref={moreButton}
            aria-expanded={moreOpen}
            aria-controls="more-sheet"
            onClick={() => setMoreOpen((open) => !open)}
          >
            <Icon name={moreOpen ? 'x-lg' : 'three-dots'} />
            <span>{ca.nav.more}</span>
          </button>
        )}
      </nav>
      {moreOpen && (
        <div
          className="more-backdrop"
          // Clicking away closes it, like Escape; the sheet stops the click so a
          // drag that ends outside does not close what it was reading.
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setMoreOpen(false);
          }}
        >
          <nav className="more-sheet" id="more-sheet" ref={sheet} aria-label={ca.nav.more}>
            {me.authenticated && (
              <p className="muted small who">{me.displayName ?? me.handle ?? ''}</p>
            )}
            {rest.map(link)}
            {me.authenticated && (
              <Link to="/signout" className="navitem">
                <Icon name="box-arrow-right" />
                <span>{ca.nav.signOut}</span>
              </Link>
            )}
            <InstallOffer />
          </nav>
        </div>
      )}
      {/* «Prova en curs»: a web test card the reader holds follows them to every page. */}
      <TestPanel />
    </div>
  );
}
